import React, { useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import {
    IDialogContentProps,
    IObjectWithKey,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Selection
} from "office-ui-fabric-react";
import { ApplicationState, BoundActionCreators, bindThunkActionCreators } from "../../../store";
import * as TimeReportingPeriodsStore from "../../../store/TimeReportingPeriodsStore";
import * as FiltersStore from "../../../store/filters";
import * as Metadata from "../../../entities/Metadata";
import { EntityType, entityTypeLabelMap } from "../../../entities/common";
import { FilterHelper } from "../../../store/timeReportingPeriod/filters";
import { contains, CommonOperations } from "../../../store/permissions";
import { isInReadonlyMode } from "../../../store/User";
import { getClearFilterId } from "../../common/withFilter";
import { IDeletionResult } from "../../../store/services/storeHelper";
import { FormatDate } from "../../utils/common";
import TimeReportingPeriodsTable from "./TimeReportingPeriodsTable";
import TimeReportingPeriodsCommandBar from "./TimeReportingPeriodsCommandBar";
import BulkCreatePeriodsPanel from "./BulkCreatePeriodsPanel";
import BackButton from "../../navigation/BackButton";
import RemoveDialog from "../../common/RemoveDialog";
import { AlertDialog } from "../../common/AlertDialog";
import { SectionControlPlaceholder } from "../../common/sectionsControl/SectionPlaceholder";
import Spinner, { ControlSpiner } from "../../common/Spinner";
import "./TimeReportingPeriodsPage.css";
import { IsTimesheetApprovalsEnabled } from "../../timeTracking/common";

type StateProps = {
    periods: TimeReportingPeriodsStore.TimeReportingPeriod[];
    isLoading: boolean;
    isCreating: boolean;
    fields: Metadata.Field[];
    filters: Metadata.IFilter<Metadata.BaseFilterValue>[];
    activeFilter?: Metadata.IFilter<Metadata.BaseFilterValue>;
    canManageConfiguration: boolean;
    canManage: boolean;
    deletionResult?: IDeletionResult[];
    createError?: string;
    timesheetApprovalsEnabled: boolean;
};

type ActionProps = {
    actions: BoundActionCreators<typeof TimeReportingPeriodsStore.actionCreators>;
    filtersActions: ReturnType<typeof FiltersStore.actionCreators.forEntity>;
};

type Props = StateProps & ActionProps;

const TimeReportingPeriodsPage = (props: Props) => {
    const {
        periods,
        isLoading,
        isCreating,
        actions,
        fields,
        filters,
        activeFilter,
        canManageConfiguration,
        canManage,
        filtersActions,
        deletionResult,
        createError,
        timesheetApprovalsEnabled
    } = props;
    const [isPanelOpen, setIsPanelOpen] = React.useState(false);
    const [periodsToRemove, setPeriodsToRemove] = React.useState<string[]>([]);
    const [selectedPeriodIds, setSelectedPeriodIds] = React.useState<string[]>([]);

    const selection = React.useMemo(() => {
        const sel = new Selection({
            onSelectionChanged: () => {
                setSelectedPeriodIds((sel.getSelection() as TimeReportingPeriodsStore.TimeReportingPeriod[]).map(_ => _.id));
            },
            getKey: (_: IObjectWithKey) => (_ as TimeReportingPeriodsStore.TimeReportingPeriod).id
        });
        return sel;
    }, []);

    const filterHelper = React.useMemo(() => new FilterHelper({ fields }), [fields]);

    const filteredPeriods = React.useMemo(
        () => filterHelper.filter(periods, activeFilter),
        [filterHelper, periods, activeFilter]
    );

    useEffect(() => {
        actions.loadPeriods();
    }, []);

    const entityLabel = entityTypeLabelMap[EntityType.TimeReportingPeriod];
    const buildRemoveDialogContentProps = (ids: string[]): IDialogContentProps => ({
        title: `Delete ${entityLabel.plural.toLowerCase()}`,
        subText: `Are you sure you want to delete ${ids.length} selected ${(ids.length === 1 ? entityLabel.singular : entityLabel.plural).toLowerCase()}?`
    });

    const notDeleted = deletionResult?.filter(_ => !_.isDeleted);

    const onClearFilter = useCallback(() => {
        const clearFilterId = getClearFilterId(EntityType.TimeReportingPeriod, filters);
        if (clearFilterId) {
            filtersActions.setActiveFilter(clearFilterId);
        }
    }, [filters, filtersActions]);

    return (
        <div className="time-reporting-periods">
            <div className="page-navigation">
                <BackButton />
            </div>
            <h2>Time Reporting Periods</h2>
            <div className="entities-list">
                <div className="entities-list-header">
                    <TimeReportingPeriodsCommandBar
                        selectedCount={selectedPeriodIds.length}
                        onBulkCreate={() => setIsPanelOpen(true)}
                        onUpdate={update => actions.bulkUpdatePeriods(selectedPeriodIds, update)}
                        onDelete={() => setPeriodsToRemove(selectedPeriodIds)}
                        onCancelSelection={() => selection.setAllSelected(false)}
                        filters={filters}
                        activeFilter={activeFilter}
                        onFilterChange={filterId => filtersActions.setActiveFilter(filterId)}
                        canManageConfiguration={canManageConfiguration}
                        canManage={canManage}
                        timesheetApprovalsEnabled={timesheetApprovalsEnabled}
                    />
                </div>

                <ControlSpiner isLoading={isLoading && !!periods.length} className="show-over">
                    <div className="entities-list-body list-container">
                        {!periods.length ? (
                            isLoading ? (
                                <Spinner />
                            ) : (
                                <SectionControlPlaceholder
                                    title="Time Reporting Periods section is empty"
                                    description="Time Reporting Periods have not been defined yet. Create periods to enable time tracking.">
                                    {canManage && (
                                        <PrimaryButton text="Create Periods" onClick={() => setIsPanelOpen(true)} />
                                    )}
                                </SectionControlPlaceholder>
                            )
                        ) : (
                            <TimeReportingPeriodsTable
                                fields={fields}
                                allPeriods={periods}
                                filteredPeriods={filteredPeriods}
                                selection={selection}
                                onUpdate={(id, update, options) => actions.bulkUpdatePeriods([id], update, options)}
                                onDelete={id => setPeriodsToRemove([id])}
                                onClearFilter={onClearFilter}
                                timesheetApprovalsEnabled={timesheetApprovalsEnabled}
                                canManage={canManage}
                            />
                        )}
                    </div>
                </ControlSpiner>

                {isPanelOpen && (
                    <BulkCreatePeriodsPanel
                        existingPeriods={periods}
                        isCreating={isCreating}
                        timesheetApprovalsEnabled={timesheetApprovalsEnabled}
                        onDismiss={() => !createError && setIsPanelOpen(false)}
                        onSubmit={request => {
                            actions.bulkCreatePeriods(request, () => setIsPanelOpen(false));
                        }}
                    />
                )}
                {periodsToRemove.length > 0 && (
                    <RemoveDialog
                        onClose={() => setPeriodsToRemove([])}
                        onComplete={() => {
                            actions.deletePeriods(periodsToRemove, () => {
                                selection.setAllSelected(false);
                            });
                        }}
                        dialogContentProps={buildRemoveDialogContentProps(periodsToRemove)}
                        confirmButtonProps={{ text: "Delete" }}
                    />
                )}
                {notDeleted && notDeleted.length > 0 && (
                    <RemoveDialog
                        onClose={() => actions.dismissDeletionResult()}
                        confirmButtonProps={{ text: "Got it" }}
                        modalProps={{ styles: { main: { minWidth: 500 } } }}
                        dialogContentProps={{ title: `Unable to delete ${entityLabel.plural.toLowerCase()}` }}>
                        <MessageBar messageBarType={MessageBarType.warning} isMultiline={true}>
                            {notDeleted.length === 1 ? (
                                <>
                                    Time reporting period {_formatPeriodRange(notDeleted[0].id, periods)} cannot be deleted
                                    because it has time already reported for it.
                                    <br />
                                    Please delete reported time for this period before deleting it again.
                                </>
                            ) : (
                                <>
                                    The following time reporting periods cannot be deleted because they have time already
                                    reported for them:
                                    <ul>
                                        {notDeleted.map(_ => (
                                            <li key={_.id}>{_formatPeriodRange(_.id, periods)}</li>
                                        ))}
                                    </ul>
                                    Please delete reported time for these periods before deleting them again.
                                </>
                            )}
                        </MessageBar>
                    </RemoveDialog>
                )}
                {createError && (
                    <AlertDialog
                        onDismiss={() => actions.dismissCreateError()}
                        dialogContentProps={{
                            title: "Unable to create periods",
                            subText: createError
                        }}
                    />
                )}
            </div>
        </div>
    );
};

const _formatPeriodRange = (id: string, periods: TimeReportingPeriodsStore.TimeReportingPeriod[]): string => {
    const period = periods.find(p => p.id === id);
    if (!period) {
        return id;
    }
    return `${FormatDate(period.attributes.StartDate)!} - ${FormatDate(period.attributes.EndDate)!}`;
};

const mapStateToProps = (state: ApplicationState): StateProps => {
    const fields = state.fields[EntityType.TimeReportingPeriod];
    const filters = FiltersStore.getFilter(state.filters, EntityType.TimeReportingPeriod);
    const isReadonly = isInReadonlyMode(state.user, state.tenant);

    return {
        periods: state.timeReportingPeriods.periods,
        isLoading: state.timeReportingPeriods.isLoading,
        isCreating: state.timeReportingPeriods.isCreating,
        fields: fields.allIds.map(_ => fields.byId[_]),
        filters: filters.all,
        activeFilter: filters.active.filter ?? filters.all.find(_ => _.isDefault),
        canManageConfiguration: contains(state.user.permissions.common, CommonOperations.ConfigurationManage),
        canManage: !isReadonly && contains(state.user.permissions.common, CommonOperations.Administrate),
        deletionResult: state.timeReportingPeriods.deletionResult,
        createError: state.timeReportingPeriods.createError,
        timesheetApprovalsEnabled: IsTimesheetApprovalsEnabled(state.tenant)
    };
};

const mapDispatchToProps = (dispatch: Dispatch): ActionProps => ({
    actions: bindThunkActionCreators(TimeReportingPeriodsStore.actionCreators, dispatch),
    filtersActions: bindActionCreators(FiltersStore.actionCreators.forEntity(EntityType.TimeReportingPeriod), dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(TimeReportingPeriodsPage);
